import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORT_ATTACHMENT_ACCEPT, SUPPORT_ATTACHMENT_MAX_BYTES } from '@bliks-io/contracts';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@bliks-io/ui';

const MAX_ATTACHMENT_MB = SUPPORT_ATTACHMENT_MAX_BYTES / 1024 / 1024;

/**
 * Screenshots for a reply: a paperclip inside the composer instead of a drop
 * zone, which would crowd the reply box pinned under the thread. The picker only offers the
 * accepted types; a file over the size limit is named and left out, as the
 * drop zone of a new request does.
 */
export function AttachButton({
  onAdd,
  disabled
}: {
  onAdd: (files: File[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const addPicked = (picked: FileList | null) => {
    const files = Array.from(picked ?? []);
    const tooLarge = files.filter((file) => file.size > SUPPORT_ATTACHMENT_MAX_BYTES);
    if (tooLarge.length > 0) {
      toast.error(
        t('common.dropzone.rejectedSize', {
          names: tooLarge.map((file) => file.name).join(', '),
          maxSize: MAX_ATTACHMENT_MB
        })
      );
    }
    const fitting = files.filter((file) => file.size <= SUPPORT_ATTACHMENT_MAX_BYTES);
    if (fitting.length > 0) onAdd(fitting);
    // Picking the same file again after removing it must fire a change.
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={SUPPORT_ATTACHMENT_ACCEPT}
        multiple
        hidden
        onChange={(event) => addPicked(event.target.files)}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            aria-label={t('supportTickets.detail.attach')}
            onClick={() => inputRef.current?.click()}
          >
            <Paperclip />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('supportTickets.detail.attach')}</TooltipContent>
      </Tooltip>
    </>
  );
}
